import { cn } from '@mdvip/shared/lib/utils';
import { useTranslation } from 'react-i18next';

import { formatServings } from './recipeDetail.util';

export interface RecipeMetaStripProps {
  prepTime: string | null;
  totalTime: string | null;
  /** Numeric yield. Pluralized internally as "1 serving" / "N servings". */
  servings: number | null;
  className?: string;
}

/**
 * The decision strip under a recipe's title: prep time, total time and yield as three static stat
 * cells. No serving stepper — the recipe's quantities are the vendor's verbatim text, so there is
 * nothing to scale.
 *
 * Times are the vendor's own human-readable strings and are rendered as-is; a cell whose value the
 * recipe doesn't carry is dropped rather than shown empty, and a recipe with none of the three
 * renders no strip at all.
 */
export function RecipeMetaStrip({ prepTime, totalTime, servings, className }: RecipeMetaStripProps) {
  const { t } = useTranslation('shared');

  const cells = [
    { label: t('recipes.detail.prepTime'), value: prepTime },
    { label: t('recipes.detail.totalTime'), value: totalTime },
    { label: t('recipes.detail.yieldLabel'), value: servings === null ? null : formatServings(servings, t) },
  ].filter((cell): cell is { label: string; value: string } => Boolean(cell.value));

  if (cells.length === 0) return null;

  return (
    <dl
      data-slot="recipe-meta-strip"
      className={cn(
        'flex flex-wrap items-stretch gap-x-8 gap-y-4 rounded-xl border bg-card px-5 py-4 shadow-xs',
        className,
      )}
    >
      {cells.map((cell) => (
        // `grow basis-32` with no shrink floor is what keeps each value on one line: in a narrow
        // column the cells wrap to a second row instead of breaking "25 minutes" in half.
        <div key={cell.label} className="flex grow basis-32 flex-col gap-1 whitespace-nowrap">
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{cell.label}</dt>
          <dd className="text-base font-semibold text-foreground">{cell.value}</dd>
        </div>
      ))}
    </dl>
  );
}
